import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './RenderOptionsDialog.css';
import './Button.css';
import {
  matchTransitionPreset,
  computeEditedDurationSeconds,
} from '../utils/settingsControls';
import { trackEvent } from '../analytics';
import UpgradePromptDialog from './UpgradePromptDialog';
import { UPSELL_PROMPTS } from '../upsell/triggers';
import { WATERMARK_POSITION_LABELS, MOTION_PACE_CHART, MOTION_MODES } from '../lib/operationCopy';
import { WATERMARK_POSITION_DEFAULT } from '../lib/editDraft';

// The last step before a render is charged — a RECEIPT, not an editor (#401).
//
// Transitions, deflicker and the watermark switch used to live here, which is
// the single biggest reason "what am I applying" was unclear: half the edit was
// on the page and half of it only existed while this modal was open. They are
// operation cards on the Edits tab now. What is left is what you can only
// answer at the moment of committing: how long the output is, what it costs,
// whether it overwrites the previous render, and whether it carries the
// wordmark. Every value here is a STATEMENT — there is nothing to change.

const formatDuration = (durationInSeconds) => {
  if (!Number.isFinite(durationInSeconds) || durationInSeconds <= 0) return '0:00:00';
  const h = Math.floor(durationInSeconds / 3600).toString();
  const m = Math.floor((durationInSeconds % 3600) / 60).toString().padStart(2, '0');
  const s = Math.floor(durationInSeconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const RenderOptionsDialog = ({
  fps,
  // Already resolved by the caller: the analyzed motion segments when stillness
  // trimming is on, one full-length segment when it is off — and, with capture-
  // gap crossfades on, already cut open at the gaps the worker will split at.
  // The dialog never second-guesses that — it only measures it.
  //
  // On a MOTIONPACE render this is the paced video's own clip list, counted in
  // OUTPUT frames at the paced `outputFps` (`pacedRenderPlan`, lib/editDraft.js).
  // Pacing rewrites the source before the clip chain sees it, but the chain then
  // splits and crossfades that clip exactly as it does a trim render's — so the
  // arithmetic below is the same in both modes, and only the units differ.
  motionSegments,
  outputFps,
  // The confirmed edit, owned by the Edits tab's operation cards.
  transitionFrames = 0,
  deflicker = false,
  removeWatermark = true,
  // Which mark a marked render carries, and where. Receipt inputs only — the
  // watermark card owns the choice. `null` logo = the stock Tweedia mark.
  // `watermarkLogoId` is a SLOT NAME (`logo0`..`logo4`), never a thing to
  // show; `watermarkLogoName` is the label the user gave that slot, which is
  // absent on a draft rebuilt from Firestore alone.
  watermarkLogoId = null,
  watermarkLogoName = null,
  watermarkPosition = WATERMARK_POSITION_DEFAULT,
  // Boundary crossfades (no registry entry — see operationCopy.js). Purely a
  // receipt line: the cards on the Edits tab own the switches, this only states
  // what the render will do with them.
  //
  // `captureGapTransitions` is the EFFECTIVE switch the caller reconciled from
  // both declarations (`effectiveFadeFrames`, lib/editDraft.js), and
  // `clipJoinTransitions` says which noun to use: it is true only when the
  // project really has `video.concatJoinFrames` and the user opted into fading
  // at them, in which case those joins are the boundaries being counted.
  //
  // `captureGapCount` is how many crossfades the render will ACTUALLY add, not
  // how many boundaries were detected — the caller applies the worker's rules
  // (`captureGapCrossfadeCount`, lib/editDraft.js) and counts the boundaries
  // that survive them. One inside trimmed stillness, or past the
  // MAX_CAPTURE_GAP_SPLITS cap, never becomes a fade.
  captureGapTransitions = false,
  clipJoinTransitions = false,
  captureGapCount = 0,
  // Which motion mode this render runs, as the resolved mode id from
  // `motionModeOf` — 'pace', 'trim', or null for a Motion section switched off
  // (#590). The receipt listed what the render would include but never named
  // the mode, so a user could not tell from it whether a render was MotionTrim
  // or MotionPace.
  motionMode = null,
  // #532's `motionPace` flag. The Motion row is a flag-lit surface like every
  // other MotionPace change: dark — production today — this dialog's markup is
  // byte-identical to pre-#590.
  motionPaceEnabled = false,
  // How many clip boundaries crossfade in total. Gap boundaries hard-cut
  // unless a fade switch is on, so this is not always
  // `motionSegments.length - 1` and the duration below has to be told.
  crossfadeCount = null,
  // True when the inputs above cannot yet describe the render this dialog is
  // about to charge for, so no length is stated at all.
  //
  // The one case is MotionPace with a stale speed curve: the curve is recomputed
  // SERVER-SIDE on save, so between a `maxSpeed`/`smoothness` change and the
  // Firestore snapshot the stored record describes the previous setting. Max
  // speed scales output length directly, and confirming here persists the draft
  // first — so the render comes out of a curve that does not exist yet, and any
  // number shown would be a promise about the wrong one.
  durationPending = false,
  creditCost,
  balance,
  isOverwrite = false,
  busy = false,
  canRemoveWatermark = false,
  entitlementsLoading = false,
  onConfirm,
  onCancel,
  projectId,
  // #350 upsell prompts. Effective entitlements + raw Stripe state so the
  // prompt can name the real unlock tier and route existing subscribers to the
  // portal instead of a checkout the server refuses.
  entitlements,
  subscription,
}) => {
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
  const dialogRef = useRef(null);
  // Which upsell prompt is open on top of this dialog, if any (#350).
  const [upsellPrompt, setUpsellPrompt] = useState(null);

  const shortfall = Number.isFinite(balance) && Number.isFinite(creditCost) && balance < creditCost
    ? creditCost - balance
    : 0;
  const cannotAfford = shortfall > 0;
  // The credits door needs a pending-window guard: until the users/{uid}
  // snapshot lands every account resolves to Free, so the prompt would pitch a
  // plan to a paying customer and offer an existing subscriber a checkout
  // create_subscription_checkout refuses.
  const creditsUpsellReady = !entitlementsLoading;

  const finalDurationSeconds = useMemo(
    () => computeEditedDurationSeconds(
      motionSegments, outputFps || safeFps, transitionFrames, crossfadeCount,
    ),
    [motionSegments, outputFps, safeFps, transitionFrames, crossfadeCount]
  );

  // An upsell prompt is stacked on top of this dialog, so Escape / an overlay
  // click belongs to the prompt — dismissing the render dialog out from under
  // it would throw away the settings the user just configured.
  const handleCancel = useCallback(() => {
    if (busy || upsellPrompt) return;
    trackEvent('render_options_cancelled', { project_id: projectId });
    onCancel();
  }, [busy, upsellPrompt, onCancel, projectId]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleCancel]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleCancel();
  };

  // No entitlements-pending guard any more. It existed because this dialog
  // owned the watermark switch and could send an over-pessimistic
  // `removeWatermark: false` for an entitled account whose snapshot hadn't
  // landed. The preference now comes from the watermark card, which defaults to
  // "remove" and can only be un-checked by an account that is already known to
  // be entitled — so the value arriving here is never worse than the truth, and
  // the worker re-resolves it authoritatively anyway (#343).
  const handleConfirm = () => {
    if (busy || cannotAfford) return;
    trackEvent('render_options_confirmed', {
      preset_id: matchTransitionPreset(transitionFrames, safeFps),
      transition_frames: transitionFrames,
      deflicker: !!deflicker,
      remove_watermark: canRemoveWatermark && !!removeWatermark,
      project_id: projectId,
    });
    onConfirm();
  };

  // The pre-render price preview is where the empty balance is discovered, so
  // it is where the credits prompt belongs (#350).
  const openCreditsUpsell = () => {
    setUpsellPrompt(UPSELL_PROMPTS.CREDITS_EXHAUSTED);
  };

  const confirmLabel = isOverwrite ? 'Replace & Create' : 'Create Video';
  const creditsWord = creditCost === 1 ? 'credit' : 'credits';
  // A marked render names WHICH mark and WHERE ("Tweedia · Bottom right" /
  // "Summit logo · Bottom left"). The server ignores customization for
  // accounts without the entitlement — those renders always carry the stock
  // mark in the default corner, so the receipt says exactly that rather than
  // echoing a preference the render will not honour.
  const markLabel = canRemoveWatermark && watermarkLogoId
    ? (watermarkLogoName || 'Your logo')
    : 'Tweedia';
  const cornerLabel = WATERMARK_POSITION_LABELS[
    canRemoveWatermark ? watermarkPosition : WATERMARK_POSITION_DEFAULT
  ] || WATERMARK_POSITION_LABELS[WATERMARK_POSITION_DEFAULT];
  // This one row prints a three-way choice — MotionTrim / MotionPace / Off — so
  // it pair-presents the two modes and takes the pair brand, per the naming rule
  // in lib/operationCopy.js. `.name` (the mode's name), not `.badge` (the
  // collapsed card's state word, free to shorten on its own), so the receipt and
  // the Motion panel can never disagree about what a mode is called.
  //
  // "Off" is the word the Motion card's own badge uses for a section switched
  // off, and the word the watermark row below uses for a clean render — the
  // receipt restates the cards rather than introducing a synonym.
  const motionValue = motionMode === 'pace'
    ? MOTION_MODES.pace.name
    : motionMode === 'trim'
      ? MOTION_MODES.trim.name
      : 'Off';

  const watermarkValue = entitlementsLoading
    ? 'Checking your plan…'
    // "Off", the same word the watermark card's badge uses for a clean render,
    // so the receipt restates the card rather than introducing a synonym.
    : (canRemoveWatermark && removeWatermark)
      ? 'Off'
      : `${markLabel} · ${cornerLabel}`;

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="dialog-content render-options-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="render-options-title"
      >
        <header className="render-options-dialog__header">
          <h3 id="render-options-title">Create video</h3>
          <p className="render-options-dialog__subtitle">Here&apos;s what you&apos;re about to render.</p>
        </header>

        <section className="render-options-dialog__summary">
          <div className="render-options-dialog__summary-row">
            <span className="render-options-dialog__summary-label">Final video</span>
            <span
              className="render-options-dialog__summary-value"
              data-testid="final-duration"
              {...(durationPending ? { role: 'status', 'aria-live': 'polite' } : {})}
            >
              {durationPending
                ? MOTION_PACE_CHART.recalculating
                : `≈ ${formatDuration(finalDurationSeconds)}`}
            </span>
          </div>
          {motionPaceEnabled && (
            <div className="render-options-dialog__summary-row">
              <span className="render-options-dialog__summary-label">Motion</span>
              <span className="render-options-dialog__summary-value" data-testid="motion-summary">
                {motionValue}
              </span>
            </div>
          )}
          <div className="render-options-dialog__summary-row">
            <span className="render-options-dialog__summary-label">Watermark</span>
            <span className="render-options-dialog__summary-value" data-testid="watermark-summary">
              {watermarkValue}
            </span>
          </div>
          {(captureGapTransitions || clipJoinTransitions) && captureGapCount > 0 && (
            <div className="render-options-dialog__summary-row">
              <span
                className="render-options-dialog__summary-label"
                data-testid="capture-gap-label"
              >
                {clipJoinTransitions ? 'Clip joins' : 'Capture gaps'}
              </span>
              <span className="render-options-dialog__summary-value" data-testid="capture-gap-summary">
                {clipJoinTransitions
                  ? `Crossfades at ${captureGapCount} clip ${captureGapCount === 1 ? 'join' : 'joins'}`
                  : `Crossfades at ${captureGapCount} capture ${captureGapCount === 1 ? 'gap' : 'gaps'}`}
              </span>
            </div>
          )}
          <div className="render-options-dialog__summary-row render-options-dialog__summary-row--muted">
            <span>This will use {creditCost} {creditsWord}</span>
          </div>
          {isOverwrite && (
            <p className="render-options-dialog__warning" data-testid="overwrite-warning">
              Your current edited video will be replaced.
            </p>
          )}
          {cannotAfford && (
            <div className="insufficient-credits-warning" data-testid="insufficient-credits-inline">
              <p className="insufficient-credits-warning__text">
                You're <strong>{shortfall} {shortfall === 1 ? 'credit' : 'credits'} short</strong> — this video needs {creditCost}, you have {balance}.
              </p>
              {/* #350: the shortfall used to be a dead end — the confirm button
                  is disabled and nothing here offered a way out. */}
              {creditsUpsellReady && (
                <button
                  type="button"
                  className="insufficient-credits-warning__cta"
                  onClick={openCreditsUpsell}
                  data-testid="credits-upsell-trigger"
                >
                  Get more credits
                </button>
              )}
            </div>
          )}
        </section>

        <div className="dialog-buttons">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="btn btn-teal"
            disabled={busy || cannotAfford}
          >
            {busy ? <span className="spinner" aria-hidden="true"></span> : confirmLabel}
          </button>
        </div>

        {upsellPrompt && (
          <UpgradePromptDialog
            promptId={upsellPrompt}
            entitlements={entitlements}
            subscription={subscription}
            shortBy={shortfall}
            projectId={projectId}
            onTopUp={() => setUpsellPrompt(null)}
            onClose={() => setUpsellPrompt(null)}
          />
        )}
      </div>
    </div>
  );
};

export default RenderOptionsDialog;
